import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';

/**
 * Modal - centered overlay (spec §12).
 * Use for confirmations and short forms (< 5 fields).
 * Use <Drawer /> for longer/detail content.
 *
 * Props:
 *   open      - boolean
 *   onClose   - () => void
 *   title     - header text
 *   size      - 'sm' (380) | 'md' (480, default) | 'lg' (640)
 *   footer    - optional React node (typically action buttons)
 *   children  - body
 *   dismissOnBackdrop - default true
 */

const SIZES = {
  sm: 'w-[380px]',
  md: 'w-[480px]',
  lg: 'w-[640px]',
};

export default function Modal({
  open,
  onClose,
  title,
  size = 'md',
  footer,
  children,
  dismissOnBackdrop = true,
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div className="fixed inset-0 z-[950] flex items-end sm:items-center justify-center p-0 sm:p-4">
      <button
        type="button"
        aria-label="Close modal"
        onClick={dismissOnBackdrop ? onClose : undefined}
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm animate-fade-in"
        tabIndex={-1}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`relative ${SIZES[size] || SIZES.md} max-w-full bg-white rounded-t-3xl sm:rounded-2xl shadow-strong flex flex-col max-h-[92vh] animate-modal-in border border-white/60`}
      >
        {title && (
          <header className="flex items-center justify-between px-5 sm:px-6 py-4 border-b border-slate-200/70">
            <h2 className="text-base font-semibold text-slate-900 tracking-tight">{title}</h2>
            <button
              type="button"
              onClick={onClose}
              className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition"
              aria-label="Close"
            >
              <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
            </button>
          </header>
        )}
        <div className="flex-1 overflow-y-auto px-5 sm:px-6 py-5">
          {children}
        </div>
        {footer && (
          <footer className="border-t border-slate-200/70 px-5 sm:px-6 py-3 flex items-center justify-end gap-2 bg-slate-50/60 rounded-b-2xl">
            {footer}
          </footer>
        )}
      </div>
    </div>,
    document.body,
  );
}
